import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Video, ListChecks, CheckSquare } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import RescheduleMeetingModal from '@/components/meetings/RescheduleMeetingModal'
import { meetingService } from '@/services/meetingService'
import { matchingService } from '@/services/matchingService'
import { planService } from '@/services/planService'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/Auth0Context'
import { Meeting } from '@/types/meeting'

const CANCELLABLE_STATUSES = ['scheduled', 'in_progress']

const MEETING_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  regular_checkin: 'Regular Check-in',
  project_review: 'Project Review',
  career_guidance: 'Career Guidance',
  skill_assessment: 'Skill Assessment',
  goal_setting: 'Goal Setting',
  feedback_session: 'Feedback Session',
  other: 'Other'
}

const MEETING_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled'
}

const MEETING_STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-green-100 text-green-700',
  in_progress: 'bg-accent-100 text-accent-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  rescheduled: 'bg-yellow-100 text-yellow-700'
}

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })

const formatTime = (value: string): string =>
  new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

const formatDueDate = (value: string): string =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const MeetingDetailView: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>()
  const { showToast } = useToast()
  const { user } = useAuth()

  const [meeting, setMeeting] = useState<Meeting | null>(null)
  const [loading, setLoading] = useState(true)
  const [namesById, setNamesById] = useState<Record<string, string>>({})
  const [isCancelling, setIsCancelling] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [showAddActionItem, setShowAddActionItem] = useState(false)
  const [newActionTitle, setNewActionTitle] = useState('')
  const [newActionAssignedTo, setNewActionAssignedTo] = useState<'mentor' | 'mentee'>('mentor')
  const [newActionDueDate, setNewActionDueDate] = useState('')
  const [isAddingActionItem, setIsAddingActionItem] = useState(false)
  const [planName, setPlanName] = useState<string | null>(null)

  const fetchMeeting = useCallback(async () => {
    if (!meetingId) return
    try {
      setLoading(true)
      const [data, relationships] = await Promise.all([
        meetingService.getMeeting(meetingId),
        matchingService.getMyRelationships()
      ])
      setMeeting(data)
      setNotesDraft(data.notes ?? '')

      const names: Record<string, string> = {}
      relationships.forEach(r => {
        if (r.mentor) names[r.mentor.id] = `${r.mentor.firstName} ${r.mentor.lastName}`
        if (r.mentee) names[r.mentee.id] = `${r.mentee.firstName} ${r.mentee.lastName}`
      })
      setNamesById(names)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load meeting'
      })
    } finally {
      setLoading(false)
    }
  }, [meetingId, showToast])

  useEffect(() => {
    fetchMeeting()
  }, [fetchMeeting])

  useEffect(() => {
    if (!meeting?.planId) {
      setPlanName(null)
      return
    }
    planService
      .getPlan(meeting.planId)
      .then(plan => setPlanName(plan.title))
      .catch(() => setPlanName(null))
  }, [meeting?.planId])

  const handleCancel = async () => {
    if (!meetingId) return
    try {
      setIsCancelling(true)
      const updated = await meetingService.cancelMeeting(meetingId)
      setMeeting(updated)
      showToast({ type: 'success', message: 'Meeting cancelled' })
      setShowCancelConfirm(false)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to cancel meeting'
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const handleToggleAgendaItem = async (itemId: string) => {
    if (!meetingId) return
    try {
      const updated = await meetingService.toggleAgendaItem(meetingId, itemId)
      setMeeting(updated)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update agenda item'
      })
    }
  }

  const handleToggleActionItem = async (itemId: string) => {
    if (!meetingId) return
    try {
      const updated = await meetingService.toggleActionItem(meetingId, itemId)
      setMeeting(updated)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update action item'
      })
    }
  }

  const handleNotesBlur = async () => {
    if (!meetingId || !meeting || notesDraft === (meeting.notes ?? '')) return
    try {
      const updated = await meetingService.updateMeetingNotes(meetingId, notesDraft)
      setMeeting(updated)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save notes'
      })
    }
  }

  const handleAddActionItem = async () => {
    if (!meetingId || !newActionTitle.trim()) return
    try {
      setIsAddingActionItem(true)
      const updated = await meetingService.addActionItem(meetingId, {
        title: newActionTitle.trim(),
        assignedTo: newActionAssignedTo,
        dueDate: newActionDueDate ? new Date(newActionDueDate).toISOString() : undefined
      })
      setMeeting(updated)
      setNewActionTitle('')
      setNewActionDueDate('')
      setShowAddActionItem(false)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to add action item'
      })
    } finally {
      setIsAddingActionItem(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">Meeting not found.</p>
        </div>
      </div>
    )
  }

  const counterpartId = meeting.mentorId === user?.id ? meeting.menteeId : meeting.mentorId
  const counterpartName = namesById[counterpartId]

  const sortedAgenda = [...meeting.agenda].sort((a, b) => a.order - b.order)

  const isCreator = meeting.createdBy === user?.id
  const canManage = isCreator && CANCELLABLE_STATUSES.includes(meeting.status)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Link
          to="/meetings"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to meetings
        </Link>

        <Card className="mb-6">
          <CardBody>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {MEETING_TYPE_LABELS[meeting.type] || meeting.type}
                  </h1>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      MEETING_STATUS_STYLES[meeting.status] || 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
                  </span>
                </div>
                {counterpartName && (
                  <p className="mt-1 text-gray-600">with {counterpartName}</p>
                )}
                {meeting.planId && planName && (
                  <p className="mt-1 text-sm">
                    <Link to={`/plans/${meeting.planId}`} className="text-primary-600 hover:text-primary-700 underline">
                      {planName}
                    </Link>
                  </p>
                )}
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setShowRescheduleModal(true)}>
                    Reschedule
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => setShowCancelConfirm(true)}>
                    Cancel Meeting
                  </Button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDateTime(meeting.scheduledStartTime)} – {formatTime(meeting.scheduledEndTime)}
              </span>
              <span className="flex items-center gap-1">
                <Video className="w-4 h-4" />
                {meeting.platform}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {meeting.duration} min
              </span>
            </div>

            {meeting.meetingUrl && (
              <div className="mt-3">
                <a
                  href={meeting.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline text-sm break-all"
                >
                  {meeting.meetingUrl}
                </a>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Agenda</h2>
            </div>
          </CardHeader>
          <CardBody>
            {sortedAgenda.length === 0 ? (
              <p className="text-sm text-gray-500">No agenda items.</p>
            ) : (
              <ul className="space-y-3">
                {sortedAgenda.map((item, index) => (
                  <li key={item.id ?? `${item.title}-${index}`} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      aria-label={item.title}
                      checked={!!item.isCompleted}
                      disabled={!item.id}
                      onChange={() => item.id && handleToggleAgendaItem(item.id)}
                      className="mt-1"
                    />
                    <div>
                      <span
                        className={`font-medium ${item.isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}`}
                      >
                        {item.title}
                      </span>{' '}
                      <span className="text-sm text-gray-500">({item.estimatedMinutes} min)</span>
                      {item.description && (
                        <p className="text-sm text-gray-600">{item.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
              </div>
              {!showAddActionItem && (
                <Button variant="secondary" size="sm" onClick={() => setShowAddActionItem(true)}>
                  Add action item
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {meeting.actionItems.length === 0 ? (
              <p className="text-sm text-gray-500">No action items.</p>
            ) : (
              <ul className="space-y-3">
                {meeting.actionItems.map((item, index) => (
                  <li key={item.id ?? `${item.title}-${index}`} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      aria-label={item.title}
                      checked={!!item.isCompleted}
                      disabled={!item.id}
                      onChange={() => item.id && handleToggleActionItem(item.id)}
                      className="mt-1"
                    />
                    <div>
                      <p
                        className={`font-medium ${item.isCompleted ? 'line-through text-gray-400' : 'text-gray-900'}`}
                      >
                        {item.title}
                      </p>
                      {item.description && (
                        <p className="text-sm text-gray-600">{item.description}</p>
                      )}
                      <p className="text-sm text-gray-500">
                        Assigned to {item.assignedTo}
                        {item.dueDate && ` · Due ${formatDueDate(item.dueDate)}`}
                        {item.completedAt && ` · Completed ${formatDueDate(item.completedAt)}`}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {showAddActionItem && (
              <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
                <div>
                  <label htmlFor="new-action-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    id="new-action-title"
                    type="text"
                    value={newActionTitle}
                    onChange={e => setNewActionTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="flex gap-3">
                  <div>
                    <label htmlFor="new-action-assignee" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignee
                    </label>
                    <select
                      id="new-action-assignee"
                      value={newActionAssignedTo}
                      onChange={e => setNewActionAssignedTo(e.target.value as 'mentor' | 'mentee')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="mentor">Mentor</option>
                      <option value="mentee">Mentee</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="new-action-due-date" className="block text-sm font-medium text-gray-700 mb-1">
                      Due date (optional)
                    </label>
                    <input
                      id="new-action-due-date"
                      type="date"
                      value={newActionDueDate}
                      onChange={e => setNewActionDueDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAddActionItem(false)}
                    disabled={isAddingActionItem}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddActionItem}
                    disabled={isAddingActionItem || !newActionTitle.trim()}
                    isLoading={isAddingActionItem}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
          </CardHeader>
          <CardBody>
            <label htmlFor="meeting-notes" className="sr-only">
              Notes
            </label>
            <textarea
              id="meeting-notes"
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Add notes about this meeting..."
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </CardBody>
        </Card>
      </div>

      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => !isCancelling && setShowCancelConfirm(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Cancel this meeting?</h2>
              <p className="text-sm text-gray-600 mb-6">
                This cannot be undone. The other participant will be notified.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                >
                  Keep Meeting
                </Button>
                <Button variant="danger" onClick={handleCancel} disabled={isCancelling} isLoading={isCancelling}>
                  Yes, Cancel Meeting
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {meetingId && (
        <RescheduleMeetingModal
          meetingId={meetingId}
          isOpen={showRescheduleModal}
          onClose={() => setShowRescheduleModal(false)}
          onRescheduled={result => {
            setMeeting(result.meeting)
            setShowRescheduleModal(false)
          }}
        />
      )}
    </div>
  )
}

export default MeetingDetailView
